import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';

const EventCard = ({ event, featured = false }) => {
  const {
    date,
    startDate,
    endDate,
    time,
    title,
    description,
    type,
    host,
    participants,
    status,
    link,
    tags = []
  } = event;

  const getStatusColor = (status) => {
    switch (status) {
      case 'upcoming': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'live': return 'bg-red-100 text-red-800 border-red-200 pulse-live';
      case 'completed': return 'bg-gray-100 text-gray-600 border-gray-200';
      default: return 'bg-forest-100 text-forest-800 border-forest-200';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'charity': return 'bg-gold-100 text-gold-800';
      case 'raid-train': return 'bg-purple-100 text-purple-800';
      case 'game-night': return 'bg-green-100 text-green-800';
      case 'tournament': return 'bg-red-100 text-red-800';
      case 'community': return 'bg-blue-100 text-blue-800';
      default: return 'bg-forest-100 text-forest-800';
    }
  };

  return (
    <div className={`card hover-lift ${featured ? 'border-2 border-gold-300 shadow-2xl' : ''}`}>
      {featured && (
        <div className="bg-gradient-to-r from-gold-400 to-gold-500 text-white text-center py-2 -m-6 mb-4 rounded-t-2xl">
          <span className="font-bold text-sm">🌟 FEATURED EVENT</span>
        </div>
      )}

      <div className="space-y-4">
        {/* Event Header */}
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="flex flex-wrap gap-2">
            <span className={`text-xs px-3 py-1 rounded-full font-medium border ${getStatusColor(status)}`}>
              {status === 'live' ? '🔴 LIVE' : status.toUpperCase()}
            </span>
            <span className={`text-xs px-3 py-1 rounded-full font-medium ${getTypeColor(type)}`}>
              {type.replace('-', ' ').toUpperCase()}
            </span>
          </div>
          <div style={{textAlign: 'right'}}>
            <div style={{fontWeight: '600', fontSize: '1rem', color: 'var(--forest-700)', marginBottom: '0.25rem'}}>
              {startDate || date}
              {endDate && startDate !== endDate && ` - ${endDate}`}
            </div>
            {time && (
              <div style={{fontSize: '0.875rem', color: 'var(--forest-600)'}}>
                {time}
              </div>
            )}
          </div>
        </div>

        {/* Event Title */}
        <div>
          <h3 style={{
            fontWeight: 'bold',
            color: 'var(--forest-800)',
            marginBottom: '0.5rem',
            fontSize: featured ? '1.5rem' : '1.25rem',
            lineHeight: '1.3'
          }}>
            {title}
          </h3>
          <p style={{color: 'var(--forest-600)', fontSize: '0.875rem', lineHeight: '1.4'}}>
            {description}
          </p>
        </div>

        {/* Event Details */}
        {(host || participants) && (
          <div className="flex flex-wrap gap-4 text-sm">
            {host && (
              <div>
                <span className="font-medium text-forest-700">Host:</span>{' '}
                <span className="text-forest-600">{host}</span>
              </div>
            )}
            {participants && (
              <div>
                <span className="font-medium text-forest-700">Participants:</span>{' '}
                <span className="text-forest-600">{participants}</span>
              </div>
            )}
          </div>
        )}

        {/* Tags */}
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {tags.map((tag, index) => (
              <span key={index} className="text-xs bg-earth-100 text-earth-700 px-2 py-1 rounded">
                #{tag}
              </span>
            ))}
          </div>
        )}

        {/* Action Buttons */}
        <div style={{display: 'flex', gap: '0.75rem', flexWrap: 'wrap', paddingTop: '0.5rem'}}>
          {status === 'live' && link && (
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-enhanced btn-primary"
            >
              🔴 Watch Live
            </a>
          )}
          {status === 'upcoming' && (
            <>
              <button className="btn-enhanced btn-outline">
                Set Reminder
              </button>
              {link && (
                <a
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-enhanced btn-secondary"
                >
                  Details
                </a>
              )}
            </>
          )}
          {status === 'completed' && link && (
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-enhanced btn-outline"
            >
              View Highlights
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

const FilterButton = ({ label, active, onClick, count = 0 }) => {
  return (
    <button
      onClick={onClick}
      className={`btn-enhanced ${active ? 'btn-primary' : 'btn-outline'}`}
    >
      {label} {count > 0 && `(${count})`}
    </button>
  );
};

const Events = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchEventsData();
  }, []);

  const fetchEventsData = async () => {
    try {
      setLoading(true);
      const response = await apiService.getEventsData();
      
      // Sort events by start date (newest first)
      const sortedEvents = response.data.sort((a, b) => {
        // Use startDate if available, otherwise fall back to date
        const dateA = new Date(a.startDate || a.date);
        const dateB = new Date(b.startDate || b.date);
        return dateA - dateB; // Ascending order (oldest first)
      });
      
      setEvents(sortedEvents);
      setError(null);
    } catch (error) {
      console.error('Error fetching events data:', error);
      setError('Failed to load events information. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{minHeight: '100vh'}}>
        <div className="container text-center" style={{padding: '4rem 1rem'}}>
          <div className="spinner"></div>
          <p style={{color: 'var(--forest-600)', marginTop: '1rem'}}>Loading events...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{minHeight: '100vh'}}>
        <div className="container text-center" style={{padding: '4rem 1rem'}}>
          <p style={{color: 'var(--earth-600)', marginBottom: '1rem'}}>⚠️ {error}</p>
          <button onClick={fetchEventsData} className="btn btn-outline">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const eventTypes = [
    { key: 'all', label: 'All Events' },
    { key: 'charity', label: 'Charity' },
    { key: 'raid-train', label: 'Raid Train' },
    { key: 'game-night', label: 'Game Night' },
    { key: 'tournament', label: 'Tournament' },
    { key: 'community', label: 'Community' }
  ];

  const filteredEvents = activeFilter === 'all' 
    ? events 
    : events.filter(event => event.tags && event.tags.includes(activeFilter));

  const featuredEvent = events.find(event => event.status === 'live') || events[0];
  const upcomingEvents = events.filter(event => event.status === 'upcoming');
  const liveEvents = events.filter(event => event.status === 'live');

  const getEventCount = (type) => {
    if (type === 'all') return events.length;
    return events.filter(event => event.tags && event.tags.includes(type)).length;
  };

  return (
    <div style={{minHeight: '100vh'}}>
      <div className="container" style={{padding: '4rem 1rem'}}>
        {/* Page Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-gradient mb-6">
            Community Events
          </h1>
          <p className="text-lg text-forest-600 max-w-3xl mx-auto">
            Join The Canopy for exciting community events, charity streams, raid trains, 
            and gaming tournaments. There's always something fun happening under our canopy!
          </p>
        </div>

        {/* Quick Stats */}
        {(liveEvents.length > 0 || upcomingEvents.length > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
            <div className="card text-center p-4">
              <div className="text-2xl font-bold text-red-600 mb-1">
                {liveEvents.length}
              </div>
              <div className="text-sm text-forest-600">Live Now</div>
            </div>
            <div className="card text-center p-4">
              <div className="text-2xl font-bold text-blue-600 mb-1">
                {upcomingEvents.length}
              </div>
              <div className="text-sm text-forest-600">Upcoming</div>
            </div>
            <div className="card text-center p-4">
              <div className="text-2xl font-bold text-forest-600 mb-1">
                {events.filter(e => e.tags && e.tags.includes('charity')).length}
              </div>
              <div className="text-sm text-forest-600">Charity Events</div>
            </div>
          </div>
        )}

        {/* Featured Event */}
        {featuredEvent && (
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gradient mb-6">
              {featuredEvent.status === 'live' ? '🔴 Happening Now' : '🌟 Featured Event'}
            </h2>
            <EventCard event={featuredEvent} featured />
          </div>
        )}

        {/* Filter Buttons */}
        <div className="mb-8">
          <div style={{display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap'}}>
            {eventTypes.map((type) => (
              <FilterButton
                key={type.key}
                label={type.label}
                active={activeFilter === type.key}
                onClick={() => setActiveFilter(type.key)}
                count={getEventCount(type.key)}
              />
            ))}
          </div>
        </div>

        {/* Events List */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mb-12">
          {filteredEvents
            .filter(event => event !== featuredEvent) // Don't show featured event twice
            .map((event, index) => (
              <EventCard key={index} event={event} />
            ))}
        </div>

        {filteredEvents.length === 0 && (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📅</div>
            <h3 className="text-xl font-bold text-forest-800 mb-2">
              No events found
            </h3>
            <p className="text-forest-600">
              Try adjusting your filter or check back soon for new events!
            </p>
          </div>
        )}


        {/* Community Event Ideas */}
        <div className="card">
          <h2 className="text-2xl font-bold text-gradient mb-6 text-center">
            Have an Event Idea?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            <div>
              <div className="w-12 h-12 bg-forest-100 rounded-full mx-auto mb-3 flex items-center justify-center">
                <span className="text-xl">💡</span>
              </div>
              <h3 className="font-bold text-forest-800 mb-2">Suggest an Event</h3>
              <p className="text-forest-600 text-sm">
                Got a cool idea for a community event? We'd love to hear it!
              </p>
            </div>
            <div>
              <div className="w-12 h-12 bg-gold-100 rounded-full mx-auto mb-3 flex items-center justify-center">
                <span className="text-xl">🎯</span>
              </div>
              <h3 className="font-bold text-forest-800 mb-2">Host an Event</h3>
              <p className="text-forest-600 text-sm">
                Want to host your own Canopy event? Apply to become an event organizer.
              </p>
            </div>
            <div>
              <div className="w-12 h-12 bg-earth-100 rounded-full mx-auto mb-3 flex items-center justify-center">
                <span className="text-xl">🤝</span>
              </div>
              <h3 className="font-bold text-forest-800 mb-2">Partner with Us</h3>
              <p className="text-forest-600 text-sm">
                Looking to collaborate? Reach out about partnership opportunities.
              </p>
            </div>
          </div>
          <div className="text-center mt-6">
            <a
              href="https://discord.gg/thecanopy"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-enhanced btn-secondary"
            >
              Contact Us on Discord
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Events;